import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { fetchApi, translate } from '@js/common/helpers';
import { HttpMethods } from 'tagwalk-api-client/assets/js/common/enums';
import { groupEventsByDay } from '../utils/grouping';
import { findNextEvent, getEventEnd } from '../utils/schedule';
import { CityTabs } from '../components/CityTabs';
import { DayScroller } from '../components/DayScroller';
function eventsInCity(events, citySlug) {
    return events.filter(function (event) { var _a; return ((_a = event.city) === null || _a === void 0 ? void 0 : _a.slug) === citySlug; });
}
function isUpcoming(event) {
    return getEventEnd(event).getTime() >= new Date().getTime();
}
function resolveInitialCity(events) {
    var located = events.filter(function (event) { return event.city !== null; });
    var upcoming = located.filter(isUpcoming);
    if (upcoming.length > 0) {
        return upcoming[0].city;
    }
    if (located.length > 0) {
        return located[located.length - 1].city;
    }
    return null;
}
function resolveScrollToDayKey(cityEvents) {
    if (cityEvents.length === 0) {
        return '';
    }
    var nextEvent = findNextEvent(cityEvents);
    if (nextEvent !== null) {
        return nextEvent.date.slice(0, 10);
    }
    var todayKey = new Date().toISOString().slice(0, 10);
    if (cityEvents.some(function (event) { return event.date.slice(0, 10) === todayKey; })) {
        return todayKey;
    }
    return cityEvents[cityEvents.length - 1].date.slice(0, 10);
}
export function AgendaPage(_a) {
    var agendaSlug = _a.agendaSlug, season = _a.season, type = _a.type, subtitle = _a.subtitle, userEmail = _a.userEmail, initialEvents = _a.initialEvents;
    var _b = useState(initialEvents), events = _b[0], setEvents = _b[1];
    var _c = useState(initialEvents.length === 0), loading = _c[0], setLoading = _c[1];
    var _d = useState(''), selectedCity = _d[0], setSelectedCity = _d[1];
    var _e = useState(''), scrollToDayKey = _e[0], setScrollToDayKey = _e[1];
    function selectInitialEvent(data) {
        var initialCity = resolveInitialCity(data);
        if (initialCity === null) {
            return;
        }
        setSelectedCity(initialCity.slug);
        setScrollToDayKey(resolveScrollToDayKey(eventsInCity(data, initialCity.slug)));
    }
    useEffect(function () {
        if (initialEvents.length > 0) {
            selectInitialEvent(initialEvents);
            return;
        }
        fetchApi('api_event_list_by_agenda', HttpMethods.get, { slug: agendaSlug })
            .then(function (response) { return (response.ok ? response.json() : []); })
            .then(function (data) {
            setEvents(data);
            selectInitialEvent(data);
        })
            .finally(function () { return setLoading(false); });
    }, [agendaSlug]);
    var cities = useMemo(function () {
        var seen = new Map();
        events.forEach(function (event) {
            if (event.city && !seen.has(event.city.slug)) {
                seen.set(event.city.slug, event.city);
            }
        });
        return Array.from(seen.values());
    }, [events]);
    var filteredEvents = useMemo(function () { return eventsInCity(events, selectedCity); }, [events, selectedCity]);
    var days = useMemo(function () { return groupEventsByDay(filteredEvents); }, [filteredEvents]);
    function onCityChange(citySlug) {
        setSelectedCity(citySlug);
        setScrollToDayKey(resolveScrollToDayKey(eventsInCity(events, citySlug)));
    }
    return (React.createElement("div", { className: "agenda" },
        React.createElement("header", { className: "agenda-header" },
            React.createElement("h1", null, translate('calendar.title')),
            subtitle && React.createElement("p", { className: "agenda-subtitle" }, subtitle),
            cities.length > 0 && React.createElement(CityTabs, { cities: cities, value: selectedCity, onChange: onCityChange })),
        loading ? (React.createElement("p", { className: "agenda-loading" }, translate('calendar.loading'))) : (React.createElement(DayScroller, { days: days, season: season, type: type, userEmail: userEmail, scrollToDayKey: scrollToDayKey }))));
}
